import {
  AnalyzeWithOpsPilotProps,
  Area,
  Card,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Chip,
  InfoTooltip,
  Line,
  ShadowDivider,
  Skeleton,
  TimeXAxis,
  Tooltip,
  YAxis,
  ZoomProps,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';
import { dashesOrNum } from '@cast/utils';

import { MetricsBox } from 'components/boxes/MetricsBox';
import { TimeSeriesEntryWithTicks } from 'types/metrics';

export type ChartCardLabel = {
  label: 'Fixed' | 'Dynamic' | 'Auto-expanding';
  tooltip?: string;
} | null;

type UsedMetric = {
  avg?: number;
  dataKey: string;
};

type ProvisionedMetric = UsedMetric & {
  metricsBoxTooltip?: string;
};

type ChartCardProps = {
  title: string;
  tooltip: string;
  testId: string;
  unit: string;
  metrics: {
    used: UsedMetric;
    provisioned: ProvisionedMetric;
  };
  enabledMetrics: {
    used: boolean;
    provisioned: boolean;
  };
  isCloudDisksEnabled?: boolean;
  data: object[];
  isLoading: boolean;
  hasNoData: boolean;
  syncId: string;
  timeZone: string;
  labelConfig?: ChartCardLabel;
  onMetricChange: (metrics: { used: boolean; provisioned: boolean }) => void;
  analyzeWithOpsPilot?: AnalyzeWithOpsPilotProps;
  zoomProps?: ZoomProps;
};

export const ChartCard = ({
  title,
  tooltip,
  testId,
  unit,
  metrics: { used, provisioned },
  enabledMetrics,
  isCloudDisksEnabled,
  data,
  isLoading,
  hasNoData,
  syncId,
  timeZone,
  labelConfig = {
    label: 'Dynamic',
    tooltip:
      'Adjusts automatically based on usage, up to 4 times per 24 hours.',
  },
  onMetricChange,
  analyzeWithOpsPilot,
  zoomProps,
}: ChartCardProps) => {
  return (
    <Stack gap={5}>
      <Stack direction="row" alignItems="center" gap={4}>
        <Typography variant="A1" color="textSecondary">
          {title}
        </Typography>
        <InfoTooltip title={tooltip} iconSize={12} />
      </Stack>

      <Card
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          position: 'relative',
        }}
        testId={testId}
      >
        {labelConfig ? (
          <Tooltip title={labelConfig.tooltip}>
            <Typography
              variant="P12R"
              color="textSecondary"
              data-testid="sizing-type-label"
              sx={{ position: 'absolute', top: 0, right: 0, zIndex: 1 }}
            >
              <Chip
                size="sm"
                sx={{
                  backgroundColor: 'blue.50',
                  color: 'blue.600',
                  borderRadius: '0 0 0 4px',
                }}
              >
                {isLoading ? (
                  <Skeleton variant="text" width={45} />
                ) : (
                  labelConfig.label
                )}
              </Chip>
            </Typography>
          </Tooltip>
        ) : null}
        <Stack direction="row">
          <Stack gap={16} p={16}>
            <MetricsBox
              testId="avg-used-metrics"
              title="AVG. USAGE OVER TIME"
              titleProps={{ variant: 'L10R' }}
              primaryValue={dashesOrNum({ value: used.avg, roundBy: 2 })}
              sx={{ minWidth: 188 }}
              secondaryValue={unit}
              secondaryValueProps={{ variant: 'L10R', color: 'textSecondary' }}
              markerColor="blue.200"
              primaryValueProps={{ variant: 'h6' }}
              disabled={!enabledMetrics.used}
              onClick={
                isCloudDisksEnabled
                  ? () => {
                      onMetricChange({
                        provisioned:
                          enabledMetrics.used && !enabledMetrics.provisioned
                            ? true
                            : enabledMetrics.provisioned,
                        used: !enabledMetrics.used,
                      });
                    }
                  : undefined
              }
            />
            {isCloudDisksEnabled && (
              <MetricsBox
                testId="avg-provisioned-metrics"
                title="AVG. PROVISIONING OVER TIME"
                titleProps={{ variant: 'L10R' }}
                primaryValue={dashesOrNum({
                  value: provisioned.avg,
                  roundBy: 2,
                })}
                secondaryValue={unit}
                secondaryValueProps={{
                  variant: 'L10R',
                  color: 'textSecondary',
                }}
                sx={{ minWidth: 188 }}
                markerColor="green.500"
                primaryValueProps={{ variant: 'h6' }}
                disabled={!enabledMetrics.provisioned}
                tooltip={provisioned.metricsBoxTooltip}
                onClick={
                  isCloudDisksEnabled
                    ? () =>
                        onMetricChange({
                          provisioned: !enabledMetrics.provisioned,
                          used:
                            enabledMetrics.provisioned && !enabledMetrics.used
                              ? true
                              : enabledMetrics.used,
                        })
                    : undefined
                }
              />
            )}
          </Stack>

          <ShadowDivider orientation="vertical" sx={{ height: 'unset' }} />

          <Box position="relative" height={200} margin={16} flex={1}>
            <ChartContainer
              testId={`${testId}-body`}
              data={data}
              isLoading={isLoading}
              hasNoData={hasNoData}
              syncId={syncId}
              analyzeWithOpsPilot={analyzeWithOpsPilot}
              zoomProps={zoomProps}
            >
              {enabledMetrics.used && (
                <Area
                  dataKey={used.dataKey}
                  testId={`${testId}-area-used`}
                  fill="blue.200"
                  fillOpacity={0.5}
                  stroke="blue.500"
                  strokeWidth={0.5}
                  type="step"
                  tooltipConfig={{ displayName: 'Used', unit }}
                />
              )}
              <ChartGrid hideVertical />
              {enabledMetrics.provisioned && (
                <Line
                  dataKey={provisioned.dataKey}
                  testId={`${testId}-line-provisioned`}
                  stroke="green.500"
                  strokeWidth={1.5}
                  type="step"
                  tooltipConfig={{ displayName: 'Provisioned', unit }}
                />
              )}

              <TimeXAxis unixDataKey="chartTimestampUnix" timezone={timeZone} />
              <YAxis
                tickFormatter={(v) => `${Math.round(Number(v))}`}
                unitFormatter={(v) => `${v} ${unit}`}
              />
              <ChartTooltip<TimeSeriesEntryWithTicks<object>>
                header={(props) => (
                  <ChartTooltipTimestampHeader {...props} timezone={timeZone} />
                )}
                formatValue={(v) =>
                  Array.isArray(v)
                    ? v.join(' - ')
                    : dashesOrNum({
                        value: v,
                        roundBy: 2,
                      })
                }
              />
            </ChartContainer>
          </Box>
        </Stack>
      </Card>
    </Stack>
  );
};
